import useGet from '@/core/composables/api/useGet'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { FeedbackTopicType } from '@/core/models/feedback-topic'
import type { StructuredFeedbackTemplate } from '@/modules/survey/models/structured-feedback-template'
import { defineStore } from 'pinia'
import { computed, onMounted } from 'vue'

export const useFeedbackTopicStore = defineStore('feedbackTopics', () => {
  const {
    data,
    status: loadingStatus,
    load,
    reload,
  } = useGet<StructuredFeedbackTemplate[]>('feedback/structured/templates')

  let loadingPromise: Promise<void> | null = null

  const runOnce = async (fetcher: () => Promise<void>) => {
    if (loadingPromise) {
      return loadingPromise
    }

    loadingPromise = fetcher().finally(() => {
      loadingPromise = null
    })

    return loadingPromise
  }

  const loadTemplates = async () => {
    if (loadingStatus.value === ApiRequestStatus.Success) {
      return
    }

    await runOnce(load)
  }

  const reloadTemplates = async () => {
    await runOnce(reload)
  }

  const templates = computed(() => {
    if (loadingStatus.value === ApiRequestStatus.Success) {
      return data.value
    }
    return []
  })

  const journalTemplates = computed(() => {
    return templates.value.filter((t) => t.type === FeedbackTopicType.Journal)
  })

  const evaluationTemplates = computed(() => {
    return templates.value.filter((t) => t.type === FeedbackTopicType.Evaluation)
  })

  onMounted(() => {
    loadTemplates()
  })

  return {
    templates,
    journalTemplates,
    evaluationTemplates,
    loadingStatus,
    loadTemplates,
    reloadTemplates,
  }
})
